import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, Database, CheckCircle, XCircle, Clock,
  AlertTriangle, Settings, Play, Square, BookOpen, Zap,
  ChevronDown, ChevronRight, Info, HelpCircle,
  Filter, Search, Check, ExternalLink, FileText, Loader2,
} from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';

import SyncJobsHistory from './SyncJobsHistory';

const SUB_TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'payment-sync', label: 'Payment Sync', icon: Zap },
  { id: 'bill-sync', label: 'Bill Sync', icon: FileText },
  { id: 'history', label: 'Sync History', icon: Clock },
  { id: 'docs', label: 'Technical Docs', icon: BookOpen },
];

function InfoTip({ children }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help inline-block ml-1" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{children}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function UbmSyncDashboard() {
  const [subTab, setSubTab] = useState('overview');

  // Overview state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [customerSyncing, setCustomerSyncing] = useState(null);

  // Scheduler state
  const [schedulerState, setSchedulerState] = useState(null);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [schedConfig, setSchedConfig] = useState({ incremental_interval_minutes: 60, full_sync_interval_hours: 168 });

  // Overview expandable errors
  const [expandedOverviewId, setExpandedOverviewId] = useState(null);
  const [overviewErrors, setOverviewErrors] = useState([]);
  const [overviewErrorsLoading, setOverviewErrorsLoading] = useState(false);

  // Docs drawer
  const [docsOpen, setDocsOpen] = useState(false);

  // Partner filter
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState('all');
  const [partnersLoading, setPartnersLoading] = useState(false);

  // Payment Sync
  const [paymentSyncStatus, setPaymentSyncStatus] = useState(null);
  const [paymentSyncLoading, setPaymentSyncLoading] = useState(false);
  const [paymentSyncing, setPaymentSyncing] = useState(null);
  const [pmSchedule, setPmSchedule] = useState(null);
  const [pmSchedLoading, setPmSchedLoading] = useState(false);
  const [pmSchedHour, setPmSchedHour] = useState(2);
  const [pmSchedMinute, setPmSchedMinute] = useState(0);

  // Bill Sync state
  const [billSyncStatus, setBillSyncStatus] = useState(null);
  const [billSyncLoading, setBillSyncLoading] = useState(false);
  const [billSyncing, setBillSyncing] = useState(false);
  const [gapData, setGapData] = useState(null);
  const [gapLoading, setGapLoading] = useState(false);

  // Org filter (multi-select)
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [orgFilterOpen, setOrgFilterOpen] = useState(false);

  // Fetchers
  const fetchSyncStatus = useCallback(async () => {
    setSyncLoading(true);
    try { const { data } = await api.get('/ubm/sync/status'); setSyncStatus(data); }
    catch { toast.error('Failed to load sync status'); }
    finally { setSyncLoading(false); }
  }, []);

  const fetchSchedulerState = useCallback(async () => {
    setSchedulerLoading(true);
    try {
      const { data } = await api.get('/ubm/scheduler/state');
      setSchedulerState(data);
      setSchedConfig({
        incremental_interval_minutes: data.incremental_interval_minutes || 60,
        full_sync_interval_hours: data.full_sync_interval_hours || 168,
      });
    } catch { console.error('Failed to fetch scheduler state'); }
    finally { setSchedulerLoading(false); }
  }, []);

  const fetchOverviewErrors = useCallback(async (accountId) => {
    setOverviewErrorsLoading(true);
    try {
      const { data } = await api.get(`/ubm/sync/errors?account_id=${accountId}&limit=20`);
      setOverviewErrors(data);
    } catch { setOverviewErrors([]); }
    finally { setOverviewErrorsLoading(false); }
  }, []);

  const toggleOverviewExpand = (accountId) => {
    if (expandedOverviewId === accountId) {
      setExpandedOverviewId(null);
      setOverviewErrors([]);
    } else {
      setExpandedOverviewId(accountId);
      fetchOverviewErrors(accountId);
    }
  };

  const fetchPartners = useCallback(async () => {
    setPartnersLoading(true);
    try {
      const { data } = await api.get('/ubm/partners');
      setPartners(data.partners || []);
      // Default to Anchor partner (id 14) if exists
      const anchor = (data.partners || []).find(p => p.partner_id === 14);
      if (anchor) setSelectedPartner(String(anchor.partner_id));
    } catch { console.error('Failed to load partners'); }
    finally { setPartnersLoading(false); }
  }, []);

  const fetchPaymentSyncStatus = useCallback(async () => {
    setPaymentSyncLoading(true);
    try {
      const { data } = await api.get('/ubm/payment-sync/status');
      setPaymentSyncStatus(data);
    } catch { console.error('Failed to load payment sync status'); }
    finally { setPaymentSyncLoading(false); }
  }, []);

  const fetchBillSyncStatus = useCallback(async () => {
    setBillSyncLoading(true);
    try {
      const { data } = await api.get('/bills/sync/status');
      setBillSyncStatus(data);
    } catch { console.error('Failed to load bill sync status'); }
    finally { setBillSyncLoading(false); }
  }, []);

  const fetchGapAnalysis = useCallback(async () => {
    setGapLoading(true);
    try {
      const { data } = await api.get('/bills/gap-analysis');
      setGapData(data);
    } catch { console.error('Failed to load gap analysis'); }
    finally { setGapLoading(false); }
  }, []);

  const fetchPmSchedule = useCallback(async () => {
    try {
      const { data } = await api.get('/payments/v2/sync/schedule');
      setPmSchedule(data);
      setPmSchedHour(data.hour ?? 2);
      setPmSchedMinute(data.minute ?? 0);
    } catch { console.error('Failed to load payment sync schedule'); }
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  useEffect(() => {
    if (subTab === 'overview') { fetchSyncStatus(); fetchSchedulerState(); }
    if (subTab === 'payment-sync') { fetchPaymentSyncStatus(); fetchPmSchedule(); }
    if (subTab === 'bill-sync') { fetchBillSyncStatus(); fetchGapAnalysis(); }
  }, [subTab, fetchSyncStatus, fetchSchedulerState, fetchPaymentSyncStatus]);

  // Actions
  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/sync-jobs/trigger?job_type=ubm_sync&mode=full');
      toast.success(`UBM Sync job created (${data.job_id?.slice(0,8)}). Check Sync History for progress.`);
      setTimeout(() => { fetchSyncStatus(); }, 10000);
    } catch { toast.error('Failed to trigger sync'); }
    finally { setSyncing(false); }
  };

  const handleCustomerSync = async (accountId, name) => {
    setCustomerSyncing(accountId);
    try {
      const { data } = await api.post(`/sync-jobs/trigger?job_type=ubm_sync&mode=full&account_id=${accountId}`);
      toast.success(`Sync job created for ${name}. Check Sync History for progress.`);
      setTimeout(() => { fetchSyncStatus(); }, 10000);
    } catch { toast.error(`Failed to sync ${name}`); }
    finally { setCustomerSyncing(null); }
  };

  const handleSchedulerToggle = async () => {
    try {
      if (schedulerState?.is_running) {
        await api.post('/ubm/scheduler/stop');
        toast.success('Scheduler stopped');
      } else {
        await api.post('/ubm/scheduler/start');
        toast.success('Scheduler started');
      }
      fetchSchedulerState();
    } catch { toast.error('Failed to toggle scheduler'); }
  };

  const handleSchedulerConfigure = async () => {
    try {
      await api.post('/ubm/scheduler/configure', schedConfig);
      toast.success('Scheduler configured');
      fetchSchedulerState();
    } catch { toast.error('Failed to configure scheduler'); }
  };

  const handleSchedulerTrigger = async (mode) => {
    try {
      await api.post(`/ubm/scheduler/trigger?mode=${mode}`);
      toast.success(`${mode} sync triggered via scheduler`);
      setTimeout(fetchSchedulerState, 5000);
    } catch { toast.error('Failed to trigger sync'); }
  };

  return (
    <div className="space-y-6" data-testid="ubm-sync-dashboard">
      {/* Sub-tab navigation */}
      <div className="border border-border rounded-none bg-card">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Activity className="h-6 w-6 text-primary" />
            <div>
              <h2 className="text-xl font-bold">UBM Sync Center</h2>
              <p className="text-sm text-muted-foreground">
                Unified sync management, monitoring, and documentation
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-b border-border">
          {SUB_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id)}
              data-testid={`sync-subtab-${tab.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                subTab === tab.id
                  ? 'border-primary text-primary bg-muted/30'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/20'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Partner Filter */}
      <div className="border border-border rounded-none bg-card p-4" data-testid="partner-filter-bar">
        <div className="flex items-center gap-4">
          <Label className="text-sm font-semibold text-muted-foreground whitespace-nowrap">Partner:</Label>
          <Select value={selectedPartner} onValueChange={setSelectedPartner} disabled={partnersLoading}>
            <SelectTrigger className="w-[280px] rounded-none h-9" data-testid="partner-select">
              <SelectValue placeholder="Select Partner..." />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All Partners ({partners.length})</SelectItem>
              {partners.map(p => (
                <SelectItem key={p.partner_id} value={String(p.partner_id)}>
                  {p.partner_name} ({p.org_count} orgs)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedPartner !== 'all' && (() => {
            const partnerOrgs = partners.find(p => String(p.partner_id) === selectedPartner)?.orgs || [];
            const filteredOrgs = partnerOrgs.filter(o => o.name.toLowerCase().includes(orgSearchQuery.toLowerCase()));
            const allSelected = selectedOrgIds.length === 0;
            return (
              <Popover open={orgFilterOpen} onOpenChange={setOrgFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-none h-9 min-w-[200px] justify-between" data-testid="org-filter-trigger">
                    <span className="truncate text-xs">
                      {allSelected
                        ? `All Orgs (${partnerOrgs.length})`
                        : `${selectedOrgIds.length} of ${partnerOrgs.length} Orgs`}
                    </span>
                    <Filter className="h-3 w-3 ml-2 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-0 rounded-none" align="start">
                  <div className="p-2 border-b border-border">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                      <input
                        placeholder="Search orgs..."
                        value={orgSearchQuery}
                        onChange={e => setOrgSearchQuery(e.target.value)}
                        className="w-full pl-7 pr-2 py-1.5 text-xs border border-border rounded-none bg-background"
                        data-testid="org-search-input"
                      />
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    <button
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 flex items-center gap-2 ${allSelected ? 'font-semibold' : ''}`}
                      onClick={() => { setSelectedOrgIds([]); }}
                    >
                      <div className={`w-3.5 h-3.5 border rounded-sm flex items-center justify-center ${allSelected ? 'bg-primary border-primary' : 'border-border'}`}>
                        {allSelected && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                      </div>
                      All Orgs
                    </button>
                    {filteredOrgs.map(o => {
                      const checked = selectedOrgIds.includes(o.id);
                      return (
                        <button
                          key={o.id}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 flex items-center gap-2"
                          onClick={() => {
                            setSelectedOrgIds(prev =>
                              checked ? prev.filter(id => id !== o.id) : [...prev, o.id]
                            );
                          }}
                        >
                          <div className={`w-3.5 h-3.5 border rounded-sm flex items-center justify-center ${checked ? 'bg-primary border-primary' : 'border-border'}`}>
                            {checked && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                          </div>
                          <span className="truncate">{o.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            );
          })()}
        </div>
      </div>

      {/* === OVERVIEW TAB === */}
      {subTab === 'overview' && (
        <div className="space-y-6">
          <div className="border border-border rounded-none bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Sync Overview</h3>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" className="rounded-none" onClick={() => { fetchSyncStatus(); fetchSchedulerState(); }} disabled={syncLoading} data-testid="refresh-overview">
                  <RefreshCw className={`h-4 w-4 mr-1 ${syncLoading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
                <Button className="rounded-none" onClick={handleSyncAll} disabled={syncing} data-testid="trigger-sync-all">
                  <Database className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Sync All Now'}
                </Button>
              </div>
            </div>

            {syncStatus?.last_sync?.completed_at && (
              <div className="bg-muted/50 border border-border p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Last Sync</p>
                    <p className="font-mono text-sm font-medium">{new Date(syncStatus.last_sync.completed_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant={syncStatus.last_sync.status === 'completed' ? 'default' : 'destructive'} className="rounded-none" data-testid="last-sync-status">
                      {syncStatus.last_sync.status === 'completed' ? <><CheckCircle className="h-3 w-3 mr-1" /> Completed</> : <><XCircle className="h-3 w-3 mr-1" /> {syncStatus.last_sync.status}</>}
                    </Badge>
                    <span className="text-xs text-muted-foreground capitalize">{syncStatus.last_sync.type?.replace('_', ' ')}</span>
                  </div>
                </div>
                {syncStatus.last_sync.totals && (
                  <div className="grid grid-cols-4 gap-4 mt-3 pt-3 border-t border-border">
                    {[
                      { label: 'Properties', c: syncStatus.last_sync.totals.properties_created, u: syncStatus.last_sync.totals.properties_updated },
                      { label: 'Spaces', c: syncStatus.last_sync.totals.spaces_created, u: syncStatus.last_sync.totals.spaces_updated },
                      { label: 'Meters', c: syncStatus.last_sync.totals.meters_created, u: syncStatus.last_sync.totals.meters_updated },
                      { label: 'Accounts', c: syncStatus.last_sync.totals.utility_accounts_created, u: syncStatus.last_sync.totals.utility_accounts_updated },
                    ].map(s => (
                      <div key={s.label}>
                        <p className="text-xs text-muted-foreground">{s.label}</p>
                        <p className="font-mono text-sm">
                          <span className="text-emerald-600">+{s.c || 0}</span>
                          <span className="text-muted-foreground mx-1">/</span>
                          <span className="text-blue-600">~{s.u || 0}</span>
                        </p>
                        <p className="text-[10px] text-muted-foreground">new / updated</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
              {[
                { label: 'Customers', value: syncStatus?.customer_stats?.length || 0 },
                { label: 'Properties', value: syncStatus?.customer_stats?.reduce((s, c) => s + c.properties, 0) || 0 },
                { label: 'Spaces', value: syncStatus?.customer_stats?.reduce((s, c) => s + c.spaces, 0) || 0 },
                { label: 'Meters', value: syncStatus?.customer_stats?.reduce((s, c) => s + c.meters, 0) || 0 },
                { label: 'Accounts', value: syncStatus?.customer_stats?.reduce((s, c) => s + c.utility_accounts, 0) || 0 },
                { label: 'PFs', value: syncStatus?.customer_stats?.reduce((s, c) => s + (c.payment_files || 0), 0) || 0 },
                { label: 'Bills', value: syncStatus?.customer_stats?.reduce((s, c) => s + (c.payment_bills || 0), 0) || 0 },
                { label: 'Utilities', value: syncStatus?.customer_stats?.reduce((s, c) => s + (c.payment_utilities || 0), 0) || 0 },
              ].map((s) => (
                <div key={s.label} className="bg-muted/30 border border-border p-3 text-center">
                  <p className="text-2xl font-bold font-mono">{s.value.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Customer Sync Status Table */}
          <div className="border border-border rounded-none bg-card">
            <div className="p-4 border-b border-border"><h3 className="font-semibold">Customer Sync Status</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Customer</th>
                    <th className="text-center p-3 font-medium text-xs uppercase tracking-wide">Properties</th>
                    <th className="text-center p-3 font-medium text-xs uppercase tracking-wide">Spaces</th>
                    <th className="text-center p-3 font-medium text-xs uppercase tracking-wide">Meters</th>
                    <th className="text-center p-3 font-medium text-xs uppercase tracking-wide">Accounts</th>
                    <th className="text-center p-3 font-medium text-xs uppercase tracking-wide">PFs</th>
                    <th className="text-center p-3 font-medium text-xs uppercase tracking-wide">Bills</th>
                    <th className="text-center p-3 font-medium text-xs uppercase tracking-wide">Utilities</th>
                    <th className="text-center p-3 font-medium text-xs uppercase tracking-wide w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {syncLoading ? (
                    <tr><td colSpan={9} className="text-center p-8 text-muted-foreground"><RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />Loading...</td></tr>
                  ) : !syncStatus?.customer_stats?.length ? (
                    <tr><td colSpan={9} className="text-center p-8 text-muted-foreground">No UBM-enabled customers found.</td></tr>
                  ) : (
                    syncStatus.customer_stats.filter(cust => {
                      if (selectedPartner === 'all' && selectedOrgIds.length === 0) return true;
                      const partner = partners.find(p => String(p.partner_id) === selectedPartner);
                      const partnerMatch = selectedPartner === 'all' || partner?.orgs?.some(o => o.id === cust.account_id);
                      const orgMatch = selectedOrgIds.length === 0 || selectedOrgIds.includes(cust.account_id);
                      return partnerMatch && orgMatch;
                    }).map((cust) => {
                      const isOverviewExpanded = expandedOverviewId === cust.account_id;
                      return (
                        <OverviewCustomerRow
                          key={cust.account_id}
                          cust={cust}
                          isExpanded={isOverviewExpanded}
                          onToggleExpand={() => toggleOverviewExpand(cust.account_id)}
                          onSync={() => handleCustomerSync(cust.account_id, cust.organisation_name)}
                          syncing={customerSyncing === cust.account_id}
                        />
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Scheduler Panel */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="scheduler-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Settings className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-lg">
                  Automated Scheduler
                  <InfoTip>
                    The scheduler runs in the background and automatically triggers syncs at configured intervals. 
                    It checks every 60 seconds whether a sync is due. After a backend restart, the scheduler must be manually re-started.
                  </InfoTip>
                </h3>
                <Badge variant={schedulerState?.is_running ? 'default' : 'secondary'} className="rounded-none" data-testid="scheduler-status-badge">
                  {schedulerState?.is_running ? 'Running' : 'Stopped'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="rounded-none" onClick={() => handleSchedulerTrigger('incremental')} data-testid="scheduler-trigger-incremental">
                  <Zap className="h-3 w-3 mr-1" /> Incremental Now
                  <InfoTip>
                    Runs an incremental sync immediately. Only fetches records changed since the last sync (uses watermarks). 
                    Fast but won't catch deletions in MySQL.
                  </InfoTip>
                </Button>
                <Button variant="outline" size="sm" className="rounded-none" onClick={() => handleSchedulerTrigger('full')} data-testid="scheduler-trigger-full">
                  <Database className="h-3 w-3 mr-1" /> Full Now
                  <InfoTip>
                    Runs a full sync immediately. Re-processes ALL records from MySQL regardless of watermarks.
                    Slower but catches deletions and schema changes. Recommended weekly.
                  </InfoTip>
                </Button>
                <Button size="sm" className="rounded-none" variant={schedulerState?.is_running ? 'destructive' : 'default'} onClick={handleSchedulerToggle} disabled={schedulerLoading} data-testid="scheduler-toggle">
                  {schedulerState?.is_running ? <><Square className="h-3 w-3 mr-1" /> Stop</> : <><Play className="h-3 w-3 mr-1" /> Start</>}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Incremental Interval (minutes)
                    <InfoTip>How often the scheduler runs an incremental sync. Minimum 5 minutes. Default 60 minutes.</InfoTip>
                  </Label>
                  <Input type="number" className="rounded-none mt-1" value={schedConfig.incremental_interval_minutes}
                    onChange={(e) => setSchedConfig({ ...schedConfig, incremental_interval_minutes: parseInt(e.target.value) || 60 })}
                    min={5} data-testid="scheduler-incr-interval" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Full Sync Interval (hours)
                    <InfoTip>How often a full re-sync runs. Catches deletions that incremental misses. Default 168 hours (weekly).</InfoTip>
                  </Label>
                  <Input type="number" className="rounded-none mt-1" value={schedConfig.full_sync_interval_hours}
                    onChange={(e) => setSchedConfig({ ...schedConfig, full_sync_interval_hours: parseInt(e.target.value) || 168 })}
                    min={1} data-testid="scheduler-full-interval" />
                </div>
                <Button size="sm" variant="outline" className="rounded-none" onClick={handleSchedulerConfigure} data-testid="scheduler-save-config">Save Configuration</Button>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Status:</span><span className="font-medium">{schedulerState?.status || 'stopped'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Runs:</span><span className="font-mono">{schedulerState?.total_runs || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Last Incremental:</span><span className="font-mono text-xs">{schedulerState?.last_incremental_run ? new Date(schedulerState.last_incremental_run).toLocaleString() : 'Never'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Last Full:</span><span className="font-mono text-xs">{schedulerState?.last_full_run ? new Date(schedulerState.last_full_run).toLocaleString() : 'Never'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Next Incremental:</span><span className="font-mono text-xs">{schedulerState?.next_incremental_run ? new Date(schedulerState.next_incremental_run).toLocaleString() : '-'}</span></div>
                {schedulerState?.last_error && (
                  <div className="mt-2 p-2 bg-destructive/10 border border-destructive/20 text-destructive text-xs">{schedulerState.last_error}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === PAYMENT SYNC TAB === */}
      {subTab === 'payment-sync' && (
        <div className="space-y-6">
          <div className="border border-border rounded-none bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">
                Payment File Sync Status
                <InfoTip>Shows payment file sync status per organization. Trigger manual syncs or view PF counts.</InfoTip>
              </h3>
              <Button variant="outline" size="sm" className="rounded-none" onClick={fetchPaymentSyncStatus} disabled={paymentSyncLoading} data-testid="refresh-payment-sync">
                <RefreshCw className={`h-4 w-4 mr-1 ${paymentSyncLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>

            {paymentSyncLoading ? (
              <div className="text-center p-8 text-muted-foreground"><RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />Loading payment sync data...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Organization</th>
                      <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">In Review</th>
                      <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Approved</th>
                      <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Paid</th>
                      <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">Total PFs</th>
                      <th className="text-right p-3 font-medium text-xs uppercase tracking-wide">
                        MySQL vs Synced
                        <InfoTip>Compares record counts in the MySQL source with what has been synced to MongoDB. Format: Bills (MySQL/Synced) | Utils (MySQL/Synced)</InfoTip>
                      </th>
                      <th className="p-3 font-medium text-xs uppercase tracking-wide text-center">Last Sync</th>
                      <th className="p-3 font-medium text-xs uppercase tracking-wide text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(paymentSyncStatus?.org_statuses || [])
                      .filter(org => {
                        const partnerMatch = selectedPartner === 'all' || String(org.partner_id) === selectedPartner;
                        const orgMatch = selectedOrgIds.length === 0 || selectedOrgIds.includes(org.account_id);
                        return partnerMatch && orgMatch;
                      })
                      .map((org) => {
                        const mysql = org.mysql_counts || {};
                        const hasMysql = mysql.pf_count !== undefined;
                        const pfMatch = !hasMysql || mysql.pf_count === org.total_pfs;
                        const billMatch = !hasMysql || mysql.bill_count === org.synced_bills;
                        const utilMatch = !hasMysql || mysql.util_count === org.synced_utilities;
                        return (
                        <tr key={org.account_id} className="border-t border-border hover:bg-muted/30">
                          <td className="p-3">
                            <p className="font-medium">{org.org_name}</p>
                            <p className="text-xs text-muted-foreground">Partner: {partners.find(p => p.partner_id === org.partner_id)?.partner_name || org.partner_id}</p>
                          </td>
                          <td className="p-3 text-right font-mono text-sm">
                            {org.pf_counts?.review || 0}
                          </td>
                          <td className="p-3 text-right font-mono text-sm">
                            {org.pf_counts?.approved || 0}
                          </td>
                          <td className="p-3 text-right font-mono text-sm">
                            {org.pf_counts?.paid || 0}
                          </td>
                          <td className="p-3 text-right font-mono text-sm font-bold">
                            <span className={!pfMatch ? 'text-destructive' : ''}>{org.total_pfs}</span>
                            {hasMysql && !pfMatch && <span className="text-[10px] text-muted-foreground ml-1">/{mysql.pf_count}</span>}
                          </td>
                          <td className="p-3 text-right">
                            {hasMysql ? (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className={`font-mono text-xs ${!billMatch ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                                  Bills: {org.synced_bills || 0}/{mysql.bill_count || 0}
                                </span>
                                <span className={`font-mono text-xs ${!utilMatch ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                                  Utils: {org.synced_utilities || 0}/{mysql.util_count || 0}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">N/A</span>
                            )}
                          </td>
                          <td className="p-3 text-center text-xs text-muted-foreground">
                            {org.last_sync ? new Date(org.last_sync).toLocaleString() : 'Never'}
                          </td>
                          <td className="p-3 text-center">
                            <Button
                              variant="ghost" size="sm" className="rounded-none h-7 text-xs"
                              onClick={async () => {
                                setPaymentSyncing(org.account_id);
                                try {
                                  await api.post(`/sync-jobs/trigger?job_type=payment_sync&mode=incremental&account_id=${org.account_id}`);
                                  toast.success(`Payment sync job created for ${org.org_name}. Check Sync History.`);
                                  setTimeout(() => fetchPaymentSyncStatus(), 5000);
                                } catch { toast.error(`Failed to sync ${org.org_name}`); }
                                finally { setPaymentSyncing(null); }
                              }}
                              disabled={paymentSyncing === org.account_id}
                              data-testid={`payment-sync-${org.account_id}`}
                            >
                              <RefreshCw className={`h-3 w-3 mr-1 ${paymentSyncing === org.account_id ? 'animate-spin' : ''}`} />
                              {paymentSyncing === org.account_id ? 'Syncing...' : 'Sync'}
                            </Button>
                          </td>
                        </tr>
                        );
                      })}
                    {(paymentSyncStatus?.org_statuses || []).filter(org => {
                      const partnerMatch = selectedPartner === 'all' || String(org.partner_id) === selectedPartner;
                      const orgMatch = selectedOrgIds.length === 0 || selectedOrgIds.includes(org.account_id);
                      return partnerMatch && orgMatch;
                    }).length === 0 && (
                      <tr><td colSpan={8} className="text-center p-8 text-muted-foreground">No organizations with payment files found for this partner.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Scheduler Settings */}
          <div className="border border-border rounded-none bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">
                Auto Sync Schedule
                <InfoTip>Configure automatic daily payment sync. All times are in UTC.</InfoTip>
              </h3>
            </div>
            {pmSchedule ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">Sync Time (UTC)</label>
                    <div className="flex gap-2">
                      <select
                        value={pmSchedHour}
                        onChange={e => setPmSchedHour(parseInt(e.target.value))}
                        className="border border-border bg-background px-3 py-1.5 text-sm rounded-none w-20"
                        data-testid="sched-hour"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                        ))}
                      </select>
                      <span className="self-center text-muted-foreground font-bold">:</span>
                      <select
                        value={pmSchedMinute}
                        onChange={e => setPmSchedMinute(parseInt(e.target.value))}
                        className="border border-border bg-background px-3 py-1.5 text-sm rounded-none w-20"
                        data-testid="sched-minute"
                      >
                        {[0, 15, 30, 45].map(m => (
                          <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold border rounded-none ${
                        pmSchedule.enabled
                          ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                          : 'bg-muted text-muted-foreground border-border'
                      }`}>
                        {pmSchedule.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      {pmSchedule.next_run_at && (
                        <span className="text-xs text-muted-foreground">
                          Next: {new Date(pmSchedule.next_run_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">Last Auto Sync</label>
                    <div className="text-sm">
                      {pmSchedule.last_run_at ? (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs">{new Date(pmSchedule.last_run_at).toLocaleString()}</span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-none ${
                            pmSchedule.last_run_status === 'done'
                              ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                              : 'bg-red-500/10 text-red-700 border-red-500/20'
                          }`}>
                            {pmSchedule.last_run_status}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">Never run</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button
                    variant={pmSchedule.enabled ? "destructive" : "default"}
                    size="sm"
                    className="rounded-none"
                    disabled={pmSchedLoading}
                    data-testid="toggle-schedule"
                    onClick={async () => {
                      setPmSchedLoading(true);
                      try {
                        const newEnabled = !pmSchedule.enabled;
                        const { data } = await api.put('/payments/v2/sync/schedule', null, {
                          params: { enabled: newEnabled, hour: pmSchedHour, minute: pmSchedMinute }
                        });
                        setPmSchedule(data);
                        toast.success(newEnabled ? `Auto sync enabled at ${String(pmSchedHour).padStart(2,'0')}:${String(pmSchedMinute).padStart(2,'0')} UTC` : 'Auto sync disabled');
                      } catch { toast.error('Failed to update schedule'); }
                      finally { setPmSchedLoading(false); }
                    }}
                  >
                    {pmSchedLoading ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : null}
                    {pmSchedule.enabled ? 'Disable Auto Sync' : 'Enable Auto Sync'}
                  </Button>
                  {pmSchedule.enabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none"
                      disabled={pmSchedLoading}
                      data-testid="update-schedule-time"
                      onClick={async () => {
                        setPmSchedLoading(true);
                        try {
                          const { data } = await api.put('/payments/v2/sync/schedule', null, {
                            params: { enabled: true, hour: pmSchedHour, minute: pmSchedMinute }
                          });
                          setPmSchedule(data);
                          toast.success(`Schedule updated to ${String(pmSchedHour).padStart(2,'0')}:${String(pmSchedMinute).padStart(2,'0')} UTC`);
                        } catch { toast.error('Failed to update schedule'); }
                        finally { setPmSchedLoading(false); }
                      }}
                    >
                      Update Time
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-none"
                    disabled={pmSchedLoading}
                    data-testid="run-sync-all-now"
                    onClick={async () => {
                      setPmSchedLoading(true);
                      try {
                        await api.post('/sync-jobs/trigger?job_type=payment_sync&mode=incremental');
                        toast.success('Payment sync job created. Check Sync History for progress.');
                        setTimeout(() => { fetchPaymentSyncStatus(); fetchPmSchedule(); }, 5000);
                      } catch { toast.error('Failed to run sync'); }
                      finally { setPmSchedLoading(false); }
                    }}
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${pmSchedLoading ? 'animate-spin' : ''}`} />
                    Run Sync Now (All Orgs)
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center p-4 text-muted-foreground text-sm">Loading scheduler config...</div>
            )}
          </div>
        </div>
      )}


      {/* === BILL SYNC TAB === */}
      {subTab === 'bill-sync' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold">Bill Sync Status</h3>
              <p className="text-xs text-muted-foreground">
                Syncs parsed bills from MySQL (parserdata + identifiers + charges) into MongoDB <code className="bg-muted px-1">bills</code> collection. Runs daily as part of UBM sync.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-none" onClick={fetchBillSyncStatus} disabled={billSyncLoading} data-testid="refresh-bill-sync">
                <RefreshCw className={`h-3 w-3 mr-1 ${billSyncLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button
                size="sm"
                className="rounded-none"
                disabled={billSyncing}
                onClick={async () => {
                  setBillSyncing(true);
                  try {
                    const { data } = await api.post('/bills/sync/trigger', null, { params: { mode: 'incremental' } });
                    toast.success(`Bill sync done: ${data.bills_created} created, ${data.bills_updated} updated`);
                    fetchBillSyncStatus();
                  } catch { toast.error('Bill sync failed'); }
                  finally { setBillSyncing(false); }
                }}
                data-testid="trigger-bill-sync"
              >
                {billSyncing ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Syncing...</> : <><Zap className="h-3 w-3 mr-1" /> Run Bill Sync</>}
              </Button>
            </div>
          </div>

          {billSyncLoading ? (
            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : billSyncStatus ? (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="border rounded-lg p-4 bg-card">
                  <p className="text-2xl font-bold">{(billSyncStatus.total_bills || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Total Bills Synced</p>
                </div>
                <div className="border rounded-lg p-4 bg-card">
                  <p className="text-2xl font-bold">{(billSyncStatus.orgs || []).length}</p>
                  <p className="text-xs text-muted-foreground">Organizations</p>
                </div>
                <div className="border rounded-lg p-4 bg-card">
                  <p className="text-sm font-mono">{billSyncStatus.last_synced_at ? new Date(billSyncStatus.last_synced_at).toLocaleString() : 'Never'}</p>
                  <p className="text-xs text-muted-foreground">Last Synced</p>
                </div>
              </div>

              {/* Per-org breakdown */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="text-xs font-semibold">Organization</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Bills</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Paid</TableHead>
                      <TableHead className="text-xs font-semibold text-right">% Paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(billSyncStatus.orgs || []).map((org) => (
                      <TableRow key={org.account_id}>
                        <TableCell className="text-xs font-medium">{org.org_name || org.account_id}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{org.bill_count.toLocaleString()}</TableCell>
                        <TableCell className="text-xs text-right font-mono text-emerald-600">{org.paid_count.toLocaleString()}</TableCell>
                        <TableCell className="text-xs text-right font-mono">
                          {org.bill_count > 0 ? `${Math.round((org.paid_count / org.bill_count) * 100)}%` : '0%'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="text-center p-8 text-muted-foreground text-sm">No bill sync data available. Click "Run Bill Sync" to start.</div>
          )}

          {/* Gap Analysis */}
          <div className="pt-4 border-t mt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-semibold">Invoice vs Bills Gap Analysis</h3>
                <p className="text-xs text-muted-foreground">
                  Compares invoiced accounts (UBM: active CA + Unit with bill in 45d) against parsed bills.
                  <b> Gap</b> = invoiced but no bill. <b>Extra</b> = has bill but not in UBM.
                </p>
              </div>
              <Button variant="outline" size="sm" className="rounded-none" onClick={fetchGapAnalysis} disabled={gapLoading}>
                <RefreshCw className={`h-3 w-3 mr-1 ${gapLoading ? 'animate-spin' : ''}`} /> Refresh Gap
              </Button>
            </div>

            {gapLoading ? (
              <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : gapData ? (
              <div className="space-y-3">
                {/* Totals summary */}
                <div className="grid grid-cols-6 gap-3">
                  {[
                    { label: 'CA Accounts', val: gapData.totals.ca_accounts },
                    { label: 'Unit Accounts', val: gapData.totals.unit_accounts },
                    { label: 'Total Invoiced', val: gapData.totals.invoiced },
                    { label: 'With Bills', val: gapData.totals.bills },
                    { label: 'Gap (no bill)', val: gapData.totals.gap, color: gapData.totals.gap > 0 ? 'text-red-600' : '' },
                    { label: 'Extra (no UBM)', val: gapData.totals.extra, color: gapData.totals.extra > 0 ? 'text-amber-600' : '' },
                  ].map(({ label, val, color }) => (
                    <div key={label} className="border rounded-lg p-3 bg-card text-center">
                      <p className={`text-lg font-bold ${color || ''}`}>{(val || 0).toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>

                {/* Per-org table */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead className="text-xs font-semibold">Organization</TableHead>
                        <TableHead className="text-xs font-semibold text-right">CA</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Unit</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Invoiced</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Bills</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Gap</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Extra</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Gap%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(gapData.orgs || []).map(org => (
                        <TableRow key={org.account_id} className={org.gap > 20 ? 'bg-red-50/30' : ''}>
                          <TableCell className="text-xs font-medium">{org.org_name}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{org.ca_accounts.toLocaleString()}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{org.unit_accounts.toLocaleString()}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{org.invoiced.toLocaleString()}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{org.bills.toLocaleString()}</TableCell>
                          <TableCell className={`text-xs text-right font-mono font-semibold ${org.gap > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{org.gap.toLocaleString()}</TableCell>
                          <TableCell className={`text-xs text-right font-mono ${org.extra > 0 ? 'text-amber-600' : ''}`}>{org.extra.toLocaleString()}</TableCell>
                          <TableCell className={`text-xs text-right font-mono ${org.gap_pct > 30 ? 'text-red-600 font-semibold' : ''}`}>{org.gap_pct}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* === SYNC HISTORY TAB (Job-based with accordion) === */}
      {subTab === 'history' && (
        <SyncJobsHistory />
      )}

      {/* === TECHNICAL DOCS TAB === */}
      {subTab === 'docs' && (
        <div className="space-y-6">
          {/* Architecture Overview Cards */}
          <div className="border border-border rounded-none bg-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <BookOpen className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-lg">Sync Architecture</h3>
              </div>
              <Button variant="outline" size="sm" className="rounded-none" onClick={() => setDocsOpen(true)} data-testid="open-docs-drawer">
                <BookOpen className="h-4 w-4 mr-1" /> Full Technical Reference
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DocCard title="Unified Sync Job Framework" content="All sync operations (UBM + Payment) are tracked as jobs in MongoDB (sync_jobs, sync_job_logs). Status lifecycle: queued > running > completed/failed. Code: /backend/services/sync_jobs/manager.py" />
              <DocCard title="Full Sync (Migration)" content="Re-processes ALL records from MySQL for each customer. Phases: Properties > Spaces > Meters > Utility Accounts > Vendor Accounts. Code: /backend/services/ubm/migration_service.py :: migrate_customer_data()" />
              <DocCard title="Incremental Sync" content="Fetches only records modified since last watermark timestamp. Fast but cannot detect MySQL deletions. Code: /backend/services/ubm/sync_service.py :: run_daily_sync()" />
              <DocCard title="Automated Scheduler" content="Asyncio background loop checking every 60s. Configurable intervals for incremental (default 60min) and full (default weekly). Must re-enable after server restart. Code: /backend/services/ubm/sync_scheduler.py" />
              <DocCard title="Overview Status Fractions" content="Shows MongoDB count / MySQL count per entity type per customer. Mismatches shown in amber. Code: /backend/services/ubm/sync_service.py :: get_sync_status()" />
              <DocCard title="Payment File Sync" content="Syncs payment files, bills, and utilities from MySQL paymentfileutilities into MongoDB pm_payment_file_* collections. Code: /backend/services/payments_v2/sync_service.py" />
            </div>
          </div>

          {/* Architecture & Workflow */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="sync-architecture-workflow">
            <div className="flex items-center gap-3 mb-6">
              <Activity className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-lg">Architecture &amp; Workflow</h3>
            </div>
            <div className="space-y-4">
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">End-to-End Sync Workflow</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-1">
                  <p>{'  '}Trigger: Manual (UI button) or Automated Scheduler</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}POST /api/sync-jobs/trigger?job_type=ubm_sync&amp;mode=full</p>
                  <p>{'  '}  File: /backend/services/sync_jobs/routes.py :: trigger_sync()</p>
                  <p>{'  '}  Creates job in sync_jobs, launches background task</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}_run_ubm_sync_with_job(job_id, mode, account_id)</p>
                  <p>{'  '}  File: /backend/services/sync_jobs/routes.py</p>
                  <p>{'  '}  Loops through each account, calls migrate_customer_data()</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}migrate_customer_data(db, mysql_customer_id, customer_unique_id, ...)</p>
                  <p>{'  '}  File: /backend/services/ubm/migration_service.py</p>
                  <p>{'  '}  |</p>
                  <p>{'  '}  +-- Phase 1: Properties (sitelist + entity) {'>'} ubm_locations</p>
                  <p>{'  '}  |     Key: bp_property_id = sitelist.UniqueId</p>
                  <p>{'  '}  +-- Phase 2: Spaces (siteunits) {'>'} ubm_spaces</p>
                  <p>{'  '}  |     Key: bp_space_id = siteunits.UniqueId</p>
                  <p>{'  '}  +-- Phase 3: Meters (entity) {'>'} ubm_meters</p>
                  <p>{'  '}  |     Key: meter_number = entity.Identifier</p>
                  <p>{'  '}  +-- Phase 4: Utility Accounts (entity + botaccounts) {'>'} ubm_utility_accounts</p>
                  <p>{'  '}  |     Key: account_number|commodity</p>
                  <p>{'  '}  +-- Phase 5: Vendor Accounts (botaccounts) {'>'} ubm_vendor_accounts</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}SyncJobManager.log_customer() per customer</p>
                  <p>{'  '}SyncJobManager.complete_job() when all done</p>
                  <p>{'  '}  File: /backend/services/sync_jobs/manager.py</p>
                </div>
              </div>

              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Component Architecture</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-1">
                  <p>+--------------------+     +---------------------------+</p>
                  <p>| BP MySQL (RO)      | {'<'}--{'>'} | FastAPI Backend            |</p>
                  <p>| 52.233.81.178      |     |                           |</p>
                  <p>| - customer         |     | services/ubm/             |</p>
                  <p>| - sitelist         |     |   migration_service.py    |</p>
                  <p>| - siteunits        |     |   sync_service.py         |</p>
                  <p>| - entity           |     |   sync_scheduler.py       |</p>
                  <p>| - botaccounts      |     |   sync_reliability.py     |</p>
                  <p>| - supplier         |     | services/sync_jobs/       |</p>
                  <p>| - paymentfile*     |     |   manager.py (job CRUD)   |</p>
                  <p>+--------------------+     |   routes.py (API + tasks) |</p>
                  <p>{'                           '}| services/payments_v2/     |</p>
                  <p>{'                           '}|   sync_service.py         |</p>
                  <p>{'                           '}+---------------------------+</p>
                  <p>{'                                     '}|</p>
                  <p>{'                                     '}v</p>
                  <p>{'                           '}+---------------------------+</p>
                  <p>{'                           '}| MongoDB                   |</p>
                  <p>{'                           '}| - sync_jobs (job status)  |</p>
                  <p>{'                           '}| - sync_job_logs (per-cust)|</p>
                  <p>{'                           '}| - ubm_locations/spaces/   |</p>
                  <p>{'                           '}|   meters/utility_accounts |</p>
                  <p>{'                           '}| - pm_payment_file_*       |</p>
                  <p>{'                           '}+---------------------------+</p>
                </div>
              </div>

              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Incremental vs Full Sync</h4>
                <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                  <div>
                    <p className="font-semibold text-foreground mb-1">Incremental Sync</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Uses watermark timestamps per customer/entity</li>
                      <li>MySQL: WHERE ModifiedDate &gt;= watermark</li>
                      <li>Fast, low data transfer</li>
                      <li>Cannot detect MySQL deletions</li>
                      <li>File: sync_service.py :: run_daily_sync()</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-1">Full Sync</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Ignores watermarks, fetches all records</li>
                      <li>Catches deletions and schema changes</li>
                      <li>Heavier but essential for data integrity</li>
                      <li>Recommended weekly via scheduler</li>
                      <li>File: migration_service.py :: migrate_customer_data()</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* File Reference */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="file-reference-section">
            <div className="flex items-center gap-3 mb-6">
              <Database className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-lg">Code File Reference</h3>
            </div>
            <div className="space-y-4 text-sm">
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-3">Backend Files</h4>
                <div className="space-y-2.5 text-xs text-muted-foreground font-mono">
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">services/sync_jobs/manager.py</span>
                    <span className="text-muted-foreground">- SyncJobManager: create/start/complete/fail jobs, log per-customer, list/query jobs</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">services/sync_jobs/routes.py</span>
                    <span className="text-muted-foreground">- API: POST /sync-jobs/trigger, GET /sync-jobs/list, GET /sync-jobs/{'<id>'}, GET /sync-jobs/by-customer</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">services/ubm/migration_service.py</span>
                    <span className="text-muted-foreground">- Core migration: migrate_customer_data() (5-phase), migrate_vendors(), run_full_migration()</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">services/ubm/sync_service.py</span>
                    <span className="text-muted-foreground">- get_sync_status() (Overview counts + MySQL comparison), run_daily_sync(), get_sync_history()</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">services/ubm/sync_scheduler.py</span>
                    <span className="text-muted-foreground">- Automated background scheduler with configurable intervals</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">services/ubm/sync_reliability.py</span>
                    <span className="text-muted-foreground">- SyncLock, SyncProgressTracker, SyncErrorTracker, SyncWatermark, retry_with_backoff()</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">services/payments_v2/sync_service.py</span>
                    <span className="text-muted-foreground">- Payment file sync: run_payment_sync(), MySQL paymentfile* to MongoDB pm_payment_file_*</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">services/invoices/routes.py</span>
                    <span className="text-muted-foreground">- Subscription invoices: auto-populate, PDF/CSV download, CRUD</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">services/reports/routes.py</span>
                    <span className="text-muted-foreground">- Account &amp; Bill Summary report with MongoDB caching</span>
                  </div>
                </div>
              </div>
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-3">Frontend Files</h4>
                <div className="space-y-2.5 text-xs text-muted-foreground font-mono">
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">pages/ubm/UbmSyncDashboard.js</span>
                    <span className="text-muted-foreground">- Overview (status fractions), Payment Sync tab, Scheduler controls, Technical Docs</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">pages/ubm/SyncJobsHistory.js</span>
                    <span className="text-muted-foreground">- Sync History: accordion by customer + all jobs table with type/status filters</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">pages/ubm/SyncJobDetail.js</span>
                    <span className="text-muted-foreground">- Sync Job detail page: /sync-job/:jobId with progress, customer logs, errors</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">pages/invoices/InvoicesPage.js</span>
                    <span className="text-muted-foreground">- Invoice CRUD, auto-populate, PDF/CSV download, multi-select publish</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-primary font-semibold shrink-0">pages/reports/AccountBillReport.js</span>
                    <span className="text-muted-foreground">- Account &amp; Bill Summary (12 months, partner/org filter, cache refresh)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* MongoDB Collections */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="mongodb-setup-section">
            <div className="flex items-center gap-3 mb-6">
              <Database className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-lg">MongoDB Collections &amp; MySQL Tables</h3>
            </div>
            <div className="space-y-4 text-sm">
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">MongoDB Collections</h4>
                <div className="font-mono text-xs space-y-1.5 text-muted-foreground">
                  <p className="font-semibold text-foreground mt-2">Sync Framework (New)</p>
                  <p>sync_jobs - Job metadata, status lifecycle, progress tracking</p>
                  <p>sync_job_logs - Per-customer logs within each job</p>
                  <p className="font-semibold text-foreground mt-3">UBM Entity Data</p>
                  <p>ubm_locations - Properties (index: account_id + bp_property_id)</p>
                  <p>ubm_spaces - Units (index: account_id + bp_space_id)</p>
                  <p>ubm_meters - Meters (index: account_id + meter_number)</p>
                  <p>ubm_utility_accounts - Utility accounts (index: account_id + account_number + commodity)</p>
                  <p>ubm_vendors - Global vendor registry (bp_supplier_id)</p>
                  <p>ubm_vendor_accounts - Per-org vendor accounts</p>
                  <p className="font-semibold text-foreground mt-3">Payment Data</p>
                  <p>pm_payment_files - Payment file metadata per org</p>
                  <p>pm_payment_file_bills - Supplier-level bill aggregations</p>
                  <p>pm_payment_file_utilities - Individual account-level utility records</p>
                  <p className="font-semibold text-foreground mt-3">Other</p>
                  <p>bp_invoices - Subscription invoices</p>
                  <p>bp_invoice_settings - Invoice configuration</p>
                  <p>bp_report_snapshots - Cached report data for past months</p>
                  <p>ubm_sync_watermarks - Incremental sync timestamps</p>
                  <p>ubm_scheduler_state - Scheduler configuration</p>
                </div>
              </div>
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">MySQL Tables (Read-Only Source)</h4>
                <div className="font-mono text-xs space-y-1.5 text-muted-foreground">
                  <p>customer - Org records (Id, UniqueId, Name, PartnerId)</p>
                  <p>partner - Partner groups (Anchor = Id 14)</p>
                  <p>sitelist - Properties/Sites (CustomerId = customer.UniqueId)</p>
                  <p>siteunits - Units within sites (CustomerId = customer.UniqueId)</p>
                  <p>entity - Utility accounts (CustomerUniqueId = customer.UniqueId)</p>
                  <p>botaccounts - Vendor/account metadata (CustomerUniqueId = customer.UniqueId)</p>
                  <p>supplier - Vendor/supplier registry</p>
                  <p>paymentfileutilities - Bill records per account per month</p>
                </div>
              </div>
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Key ID Mappings (MySQL to MongoDB)</h4>
                <div className="font-mono text-xs space-y-1.5 text-muted-foreground">
                  <p>customer.Id (int) {'>'} accounts.bp_org_id</p>
                  <p>customer.UniqueId (UUID) {'>'} accounts.bp_customer_unique_id</p>
                  <p>sitelist.UniqueId {'>'} ubm_locations.bp_property_id</p>
                  <p>siteunits.UniqueId {'>'} ubm_spaces.bp_space_id</p>
                  <p>entity.Id {'>'} ubm_meters.bp_meter_id / ubm_utility_accounts.bp_entity_id</p>
                  <p>entity.Identifier {'>'} ubm_meters.meter_number</p>
                  <p>entity.AccountNumber {'>'} ubm_utility_accounts.account_number</p>
                  <p>supplier.Id {'>'} ubm_vendors.bp_supplier_id</p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="faq-section">
            <div className="flex items-center gap-3 mb-6">
              <HelpCircle className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-lg">FAQ &amp; How-To Guide</h3>
            </div>
            <div className="space-y-4">
              <FaqItem q="How do I run a sync?" a='Go to the Overview tab and click "Sync All Now" for all customers, or click "Sync" next to individual customers. These create a tracked job in the Sync History tab. Code: POST /api/sync-jobs/trigger calls _run_ubm_sync_with_job() in sync_jobs/routes.py.' />
              <FaqItem q="What is the difference between Incremental and Full sync?" a="Incremental sync only fetches records modified since the last watermark (fast, cannot detect deletions). Full sync re-processes all records (slower, catches deletions). Code: Incremental uses run_daily_sync() in sync_service.py. Full uses migrate_customer_data() in migration_service.py." />
              <FaqItem q="How do the Overview fractions (e.g., 38/42) work?" a="The fraction shows MongoDB count / MySQL source count. MongoDB counts come from aggregation pipelines on ubm_locations, ubm_spaces, etc. MySQL counts come from subqueries on sitelist, siteunits, entity tables. Code: get_sync_status() in sync_service.py lines 192-359." />
              <FaqItem q="Why might fractions not match after a sync?" a="Common reasons: (1) entity records without AccountNumber are skipped in Phase 4 (utility accounts), (2) sitelist records may not match if CustomerId join key differs, (3) bulk write errors are caught silently. Check sync_job_logs for per-customer error details." />
              <FaqItem q="How do I set up the automated scheduler?" a='Overview tab > Automated Scheduler panel. Set intervals, click "Save Configuration" then "Start". After a server restart, re-enable manually. Code: sync_scheduler.py manages the asyncio loop, state stored in ubm_scheduler_state collection.' />
              <FaqItem q="What are the sync entity phases and are they linked?" a="Yes. Per customer: (1) Properties (sitelist > ubm_locations), (2) Spaces (siteunits > ubm_spaces), (3) Meters (entity > ubm_meters), (4) Utility Accounts (entity+botaccounts > ubm_utility_accounts), (5) Vendor Accounts (botaccounts > ubm_vendor_accounts). Each phase needs IDs from previous phases." />
              <FaqItem q="Where are sync job logs stored?" a="sync_jobs collection stores job-level metadata (status, progress, totals). sync_job_logs stores per-customer detailed logs within each job. The old ubm_sync_logs/ubm_sync_errors collections are legacy (deprecated). Code: SyncJobManager in sync_jobs/manager.py." />
              <FaqItem q="How does the Payment Sync work?" a="Similar job framework. Trigger via Payment Sync tab or POST /api/sync-jobs/trigger?job_type=payment_sync. Syncs MySQL paymentfileutilities into pm_payment_file_bills and pm_payment_file_utilities. Code: payments_v2/sync_service.py :: run_payment_sync()." />
            </div>
          </div>

          {/* API Reference */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="api-reference-section">
            <div className="flex items-center gap-3 mb-6">
              <Zap className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-lg">API Endpoints Reference</h3>
            </div>
            <div className="space-y-3">
              <div className="border border-border p-3 bg-muted/20">
                <p className="font-mono text-xs font-semibold text-foreground">POST /api/sync-jobs/trigger?job_type=ubm_sync&amp;mode=full&amp;account_id=...</p>
                <p className="text-xs text-muted-foreground mt-1">Trigger a sync job. Returns job_id for tracking. File: sync_jobs/routes.py</p>
              </div>
              <div className="border border-border p-3 bg-muted/20">
                <p className="font-mono text-xs font-semibold text-foreground">GET /api/sync-jobs/list?job_type=&amp;status=&amp;limit=50</p>
                <p className="text-xs text-muted-foreground mt-1">List sync jobs with optional filters. File: sync_jobs/routes.py</p>
              </div>
              <div className="border border-border p-3 bg-muted/20">
                <p className="font-mono text-xs font-semibold text-foreground">GET /api/sync-jobs/{'<job_id>'}</p>
                <p className="text-xs text-muted-foreground mt-1">Get full job details including customers and progress. File: sync_jobs/routes.py</p>
              </div>
              <div className="border border-border p-3 bg-muted/20">
                <p className="font-mono text-xs font-semibold text-foreground">GET /api/sync-jobs/by-customer</p>
                <p className="text-xs text-muted-foreground mt-1">Sync history grouped by customer (accordion view). File: sync_jobs/routes.py</p>
              </div>
              <div className="border border-border p-3 bg-muted/20">
                <p className="font-mono text-xs font-semibold text-foreground">GET /api/sync-jobs/customer/{'<account_id>'}/logs</p>
                <p className="text-xs text-muted-foreground mt-1">Per-customer sync log history. File: sync_jobs/routes.py</p>
              </div>
              <div className="border border-border p-3 bg-muted/20">
                <p className="font-mono text-xs font-semibold text-foreground">GET /api/ubm/sync/status</p>
                <p className="text-xs text-muted-foreground mt-1">Overview: per-customer entity counts + MySQL comparison fractions. File: server.py (calls sync_service.get_sync_status())</p>
              </div>
              <div className="border border-border p-3 bg-muted/20">
                <p className="font-mono text-xs font-semibold text-foreground">GET /api/reports/account-bill-summary?partner_id=&amp;org_id=</p>
                <p className="text-xs text-muted-foreground mt-1">12-month account/bill summary. Past months from cache, current month live. File: reports/routes.py</p>
              </div>
              <div className="border border-border p-3 bg-muted/20">
                <p className="font-mono text-xs font-semibold text-foreground">POST /api/invoices/{'<id>'}/auto-items/{'<account_id>'}</p>
                <p className="text-xs text-muted-foreground mt-1">Auto-populate invoice line items for a customer. File: invoices/routes.py</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Technical Docs Drawer */}
      <Sheet open={docsOpen} onOpenChange={setDocsOpen}>
        <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto" data-testid="tech-docs-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5" /> Sync Engine - Technical Reference</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-6 text-sm leading-relaxed">
            <section>
              <h3 className="font-bold text-base mb-2 border-b border-border pb-1">1. Sync Job Framework</h3>
              <p className="text-muted-foreground mb-2">All sync operations (UBM + Payment) are tracked as jobs. File: <code className="bg-muted px-1 py-0.5 text-xs">services/sync_jobs/manager.py</code></p>
              <p className="text-muted-foreground mb-2">Status lifecycle: <code className="bg-muted px-1 py-0.5 text-xs">queued &rarr; running &rarr; completed / completed_with_errors / failed</code></p>
              <p className="text-muted-foreground">Collections: <code className="bg-muted px-1 py-0.5 text-xs">sync_jobs</code> (job metadata) + <code className="bg-muted px-1 py-0.5 text-xs">sync_job_logs</code> (per-customer logs)</p>
            </section>
            <section>
              <h3 className="font-bold text-base mb-2 border-b border-border pb-1">2. Full Sync (Migration)</h3>
              <p className="text-muted-foreground mb-2">File: <code className="bg-muted px-1 py-0.5 text-xs">services/ubm/migration_service.py :: migrate_customer_data()</code></p>
              <p className="text-muted-foreground mb-2">Re-processes ALL records from MySQL. 5 sequential phases per customer: Properties, Spaces, Meters, Utility Accounts, Vendor Accounts.</p>
              <p className="text-muted-foreground"><strong>Key:</strong> Each phase generates ID mappings needed by subsequent phases. Failure in Phase 1 cascades downstream.</p>
            </section>
            <section>
              <h3 className="font-bold text-base mb-2 border-b border-border pb-1">3. Incremental Sync</h3>
              <p className="text-muted-foreground mb-2">File: <code className="bg-muted px-1 py-0.5 text-xs">services/ubm/sync_service.py :: run_daily_sync()</code></p>
              <p className="text-muted-foreground mb-2">Uses watermark timestamps (<code className="bg-muted px-1 py-0.5 text-xs">ubm_sync_watermarks</code>) per customer/entity. Only fetches MySQL rows with <code className="bg-muted px-1 py-0.5 text-xs">ModifiedDate &gt;= watermark</code>.</p>
              <p className="text-muted-foreground"><strong>Limitation:</strong> Cannot detect MySQL deletions. Periodic full syncs are required.</p>
            </section>
            <section>
              <h3 className="font-bold text-base mb-2 border-b border-border pb-1">4. Overview Status Fractions</h3>
              <p className="text-muted-foreground mb-2">File: <code className="bg-muted px-1 py-0.5 text-xs">services/ubm/sync_service.py :: get_sync_status()</code></p>
              <p className="text-muted-foreground mb-2">Compares MongoDB entity counts (aggregation pipeline) with MySQL source counts (subqueries on sitelist, siteunits, entity). Displayed as synced/total fractions with color coding.</p>
              <p className="text-muted-foreground"><strong>MySQL count queries:</strong> sitelist (properties), siteunits (spaces), entity WHERE Status='Active' (utility accounts).</p>
            </section>
            <section>
              <h3 className="font-bold text-base mb-2 border-b border-border pb-1">5. Automated Scheduler</h3>
              <p className="text-muted-foreground mb-2">File: <code className="bg-muted px-1 py-0.5 text-xs">services/ubm/sync_scheduler.py</code></p>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>Asyncio background task, checks every 60s</li>
                <li>Incremental: Default 60 min, minimum 5 min</li>
                <li>Full sync: Default 168h (weekly), minimum 1h</li>
                <li>State persisted in <code className="bg-muted px-1 py-0.5 text-xs">ubm_scheduler_state</code>. Must re-enable after restart.</li>
              </ul>
            </section>
            <section>
              <h3 className="font-bold text-base mb-2 border-b border-border pb-1">6. Payment Sync</h3>
              <p className="text-muted-foreground mb-2">File: <code className="bg-muted px-1 py-0.5 text-xs">services/payments_v2/sync_service.py :: run_payment_sync()</code></p>
              <p className="text-muted-foreground">Syncs MySQL paymentfileutilities into <code className="bg-muted px-1 py-0.5 text-xs">pm_payment_file_bills</code> (supplier-level) and <code className="bg-muted px-1 py-0.5 text-xs">pm_payment_file_utilities</code> (account-level).</p>
            </section>
            <section>
              <h3 className="font-bold text-base mb-2 border-b border-border pb-1">7. Reliability Framework</h3>
              <p className="text-muted-foreground mb-2">File: <code className="bg-muted px-1 py-0.5 text-xs">services/ubm/sync_reliability.py</code></p>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li><strong>SyncLock:</strong> MongoDB unique index + TTL. Prevents concurrent syncs.</li>
                <li><strong>SyncProgressTracker:</strong> Per-customer progress with resume capability.</li>
                <li><strong>SyncErrorTracker:</strong> Per-record failures without aborting the sync.</li>
                <li><strong>retry_with_backoff():</strong> Exponential backoff (1s base, 30s max, 3 attempts).</li>
              </ul>
            </section>
            <section>
              <h3 className="font-bold text-base mb-2 border-b border-border pb-1">8. Key ID Mappings</h3>
              <div className="font-mono text-xs space-y-1 text-muted-foreground bg-muted/50 p-3 border border-border">
                <p>customer.Id (int) &rarr; accounts.bp_org_id</p>
                <p>customer.UniqueId (UUID) &rarr; accounts.bp_customer_unique_id</p>
                <p>sitelist.UniqueId &rarr; ubm_locations.bp_property_id</p>
                <p>siteunits.UniqueId &rarr; ubm_spaces.bp_space_id</p>
                <p>entity.Id &rarr; ubm_utility_accounts.bp_entity_id</p>
                <p>entity.Identifier &rarr; ubm_meters.meter_number</p>
                <p>entity.AccountNumber &rarr; ubm_utility_accounts.account_number</p>
                <p>supplier.Id &rarr; ubm_vendors.bp_supplier_id</p>
              </div>
            </section>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* === Sub-components === */

/** Expandable customer row for the Overview table */
function OverviewCustomerRow({ cust, isExpanded, onToggleExpand, onSync, syncing }) {
  const hasMismatch = cust.mysql && Object.keys(cust.mysql).length > 0 && (
    cust.properties !== cust.mysql.properties ||
    cust.spaces !== cust.mysql.spaces ||
    cust.utility_accounts !== cust.mysql.utility_accounts
  );
  const [recentLogs, setRecentLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    if (isExpanded && recentLogs.length === 0) {
      setLogsLoading(true);
      api.get(`/sync-jobs/customer/${cust.account_id}/logs?limit=5`)
        .then(({ data }) => setRecentLogs(data.logs || []))
        .catch(() => {})
        .finally(() => setLogsLoading(false));
    }
  }, [isExpanded, cust.account_id]);

  return (
    <>
      <tr className={`border-t border-border hover:bg-muted/30 cursor-pointer ${isExpanded ? 'bg-muted/20' : ''}`} onClick={onToggleExpand} data-testid={`overview-row-${cust.account_id}`}>
        <td className="p-3">
          <div className="flex items-center gap-2">
            {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
            <div>
              <p className="font-medium text-sm">{cust.organisation_name}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{cust.last_synced_at ? `Last sync: ${new Date(cust.last_synced_at).toLocaleString()}` : 'Never synced'}</p>
            </div>
          </div>
        </td>
        <SyncCountCell synced={cust.properties} mysql={cust.mysql?.properties} />
        <SyncCountCell synced={cust.spaces} mysql={cust.mysql?.spaces} />
        <SyncCountCell synced={cust.meters} mysql={cust.mysql?.meters} />
        <SyncCountCell synced={cust.utility_accounts} mysql={cust.mysql?.utility_accounts} />
        <SyncCountCell synced={cust.payment_files} mysql={cust.mysql?.payment_files} />
        <SyncCountCell synced={cust.payment_bills} mysql={cust.mysql?.payment_bills} />
        <SyncCountCell synced={cust.payment_utilities} mysql={cust.mysql?.payment_utilities} />
        <td className="p-3 text-center">
          <Button variant="ghost" size="sm" className="rounded-none h-7 text-xs" onClick={(e) => { e.stopPropagation(); onSync(); }} disabled={syncing} data-testid={`sync-customer-${cust.account_id}`}>
            <RefreshCw className={`h-3 w-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync'}
          </Button>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={9} className="p-0">
            <div className={`border-t border-b p-4 ${hasMismatch ? 'bg-amber-500/5 border-amber-500/20' : 'bg-muted/30 border-border'}`}>
              {logsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><RefreshCw className="h-4 w-4 animate-spin" /> Loading sync logs...</div>
              ) : recentLogs.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4 text-muted-foreground" /> No recent sync logs for {cust.organisation_name}.
                  {hasMismatch && <span className="text-amber-600 ml-2">Count mismatches may indicate unsynced MySQL changes. Try re-syncing.</span>}
                </div>
              ) : (
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    Recent Sync Logs ({recentLogs.length})
                  </h4>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-muted-foreground border-b border-border">
                        <th className="text-left py-1.5 pr-3 font-medium">Date</th>
                        <th className="text-left py-1.5 pr-3 font-medium">Type</th>
                        <th className="text-left py-1.5 pr-3 font-medium">Status</th>
                        <th className="text-right py-1.5 pr-3 font-medium">Duration</th>
                        <th className="text-left py-1.5 pr-3 font-medium">Errors</th>
                        <th className="text-center py-1.5 font-medium">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentLogs.map((log, i) => (
                        <tr key={log.log_id || i} className="border-t border-border/50">
                          <td className="py-1.5 pr-3 whitespace-nowrap text-muted-foreground">{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</td>
                          <td className="py-1.5 pr-3">
                            <span className={`px-1.5 py-0.5 rounded-none text-[10px] font-medium ${log.job_type === 'ubm_sync' ? 'bg-indigo-100 text-indigo-700' : 'bg-cyan-100 text-cyan-700'}`}>
                              {log.job_type === 'ubm_sync' ? 'UBM' : 'Payment'}
                            </span>
                          </td>
                          <td className="py-1.5 pr-3">
                            {log.status === 'completed' ? (
                              <span className="text-emerald-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> OK</span>
                            ) : (
                              <span className="text-red-600 flex items-center gap-1"><XCircle className="h-3 w-3" /> Failed</span>
                            )}
                          </td>
                          <td className="py-1.5 pr-3 text-right text-muted-foreground">{log.duration_ms ? `${(log.duration_ms / 1000).toFixed(1)}s` : '-'}</td>
                          <td className="py-1.5 pr-3">{(log.errors?.length || 0) > 0 ? <span className="text-red-600">{log.errors.length} errors</span> : <span className="text-muted-foreground">None</span>}</td>
                          <td className="py-1.5 text-center">
                            {log.source === 'scheduler' ? (
                              <SchedulerLogPopover log={log} />
                            ) : (
                              <a href={`/sync-job/${log.job_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-0.5">
                                View <ExternalLink className="h-2.5 w-2.5" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Popover showing scheduler log details instead of a broken link */
function SchedulerLogPopover({ log }) {
  const details = log.details || {};
  const hasDetails = Object.keys(details).length > 0;
  const errors = log.errors || [];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-0.5 text-[10px] font-medium" data-testid={`scheduler-log-${log.log_id}`}>
          View Logs <ExternalLink className="h-2.5 w-2.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0 rounded-none" align="end">
        <div className="p-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <Zap className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold">Scheduler Sync Log</span>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</p>
        </div>
        <div className="p-3 space-y-2 text-xs">
          {hasDetails ? (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              {Object.entries(details).map(([key, val]) => (
                <div key={key} className="flex justify-between col-span-2">
                  <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="font-mono font-medium">{val}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-[10px]">No detailed counts available.</p>
          )}
          {log.duration_ms > 0 && (
            <div className="flex justify-between border-t border-border pt-1.5 mt-1.5">
              <span className="text-muted-foreground">Duration</span>
              <span className="font-mono font-medium">{(log.duration_ms / 1000).toFixed(1)}s</span>
            </div>
          )}
          {errors.length > 0 && (
            <div className="border-t border-border pt-1.5 mt-1.5">
              <p className="text-red-600 font-medium mb-1">{errors.length} Error(s)</p>
              <div className="max-h-20 overflow-y-auto space-y-0.5">
                {errors.slice(0, 5).map((err, i) => (
                  <p key={i} className="text-[10px] text-red-500">{typeof err === 'string' ? err : JSON.stringify(err)}</p>
                ))}
                {errors.length > 5 && <p className="text-[10px] text-muted-foreground">...and {errors.length - 5} more</p>}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Shows synced/mysql comparison like "236/236" with color coding */
function SyncCountCell({ synced, mysql }) {
  const s = synced || 0;
  const m = mysql ?? null;
  const match = m === null || s === m;
  const color = m === null
    ? 'text-muted-foreground'
    : match
      ? 'text-emerald-600'
      : 'text-amber-600';
  return (
    <td className="p-3 text-center">
      <span className={`font-mono text-sm font-medium ${color}`}>
        {s}{m !== null ? `/${m}` : ''}
      </span>
      {m !== null && !match && (
        <AlertTriangle className="h-3 w-3 text-amber-500 inline-block ml-1" />
      )}
    </td>
  );
}

function DocCard({ title, content }) {
  return (
    <div className="border border-border p-4 bg-muted/20">
      <h4 className="font-semibold text-sm mb-2">{title}</h4>
      <p className="text-xs text-muted-foreground leading-relaxed">{content}</p>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border">
      <button className="w-full p-4 text-left flex items-center justify-between hover:bg-muted/20 transition-colors" onClick={() => setOpen(!open)}>
        <span className="font-medium text-sm">{q}</span>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">{a}</div>}
    </div>
  );
}

